import axios from 'axios';

import { NotificationCenterHeaderName } from '../../@paco/entities/NotificationCenter/NotificationCenter';
import { formatDate } from '../../@paco/helpers/date';
import { DateFormat } from '../../@paco/types/dateFormatTypes';
import { checkIntegrityOfShift } from '../../models';
import { getEndOfWorkDay, getStartOfWorkDay } from '../date';
import { getLoketBridgeApiUrl } from '../index';

// Used for activation, reactivation and deletion of user
export const INACTIVE = 'inactive';
export const ACTIVE = 'active';

const getUserShifts = async (userId, startDate, endDate) => {
    const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/shifts/`,
        {
            params: {
                include: ['department', 'department.group'],
                search: userId,
                startDate: formatDate(getStartOfWorkDay(startDate), DateFormat.apiDateTime),
                endDate: formatDate(getEndOfWorkDay(endDate), DateFormat.apiDateTime),
            },
        },
    );

    response.data.forEach((shift) => {
        if (!checkIntegrityOfShift(shift)) {
            throw new TypeError(`Shift ${shift.id} type mismatch`);
        }
    });

    return response;
};

function generateUserStatusBodyData(user, activityStatus) {
    return {
        type: 'users',
        id: user.id,
        attributes: {
            activityStatus,
        },
    };
}

function generateGroup(groupId) {
    if (!groupId) {
        return {
            group: {
                data: null,
            },
        };
    }

    return {
        group: {
            data: {
                type: 'groups',
                id: groupId,
            },
        },
    };
}

function generateGroupBodyData(department) {
    return {
        type: 'departments',
        attributes: {
            name: department.name,
        },
        relationships: generateGroup(department.groupId),
    };
}

const patchDepartment = (department, eventLogTrackerData) => {
    const body = generateGroupBodyData(department);
    body.id = department.id;
    return axios.patch(`${process.env.REACT_APP_API_URL}/departments/${department.id}/`, { data: body }, {
        headers: {
            [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
        },
    });
};

const patchDeactivateUser = (userId) => {
    const body = generateUserStatusBodyData(userId, INACTIVE);
    body.id = userId;
    return axios.patch(`${process.env.REACT_APP_API_URL}/users/${userId}/`, { data: body });
};

const patchReactivateUser = (userId) => {
    const body = generateUserStatusBodyData(userId, ACTIVE);
    body.id = userId;
    return axios.patch(`${process.env.REACT_APP_API_URL}/users/${userId}/`, { data: body });
};

const postDepartment = (department, eventLogTrackerData) => {
    const body = generateGroupBodyData(department);
    return axios.post(`${process.env.REACT_APP_API_URL}/departments/`, { data: body }, {
        headers: {
            [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
        },
    });
};

const postRegistrationMail = userId => axios.post(`${process.env.REACT_APP_API_URL}/users/${userId}/resend-registration-mail`);

const getAuthenticateLoket = (state, code) => axios.get(
    `${process.env.REACT_APP_API_URL}/loket/authorize?code=${code}&state=${state}`,
);

const getUsersCSV = () => axios.get(
    `${process.env.REACT_APP_API_URL}/users/export/sv4`,
);

const postUserToLoketLink = (id, eventLogTrackerData) => axios.put(
    `${getLoketBridgeApiUrl()}/employee/sync/${id}`,
    {},
    {
        headers: {
            [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
        },
    },
);

const postUsersToLoketLink = (filter, eventLogTrackerData) => axios.put(
    `${getLoketBridgeApiUrl()}/employee/sync`,
    {},
    {
        headers: {
            [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
        },
    },
);

const getPermissionGroups = () => axios.get(`${process.env.REACT_APP_API_URL}/permission-groups`, { params: { include: 'permissions' } });

const generatePlanningPreferenceBody = (weekday, start, end, comment, userId, planningPreferenceId) => (
    {
        type: 'unavailableToWorkTimeSlot',
        ...(planningPreferenceId && { id: planningPreferenceId }),
        attributes: {
            weekday,
            start,
            end,
            comment,
        },
        relationships: {
            user: {
                data: {
                    type: 'users',
                    id: userId,
                },
            },
        },
    }
);

const patchUserPlanningPreference = (weekday, start, end, comment, userId, planningPreferenceId) => {
    const data = generatePlanningPreferenceBody(weekday, start, end, comment, userId, planningPreferenceId);
    return axios.patch(`${process.env.REACT_APP_API_URL}/unavailable-to-work-time-slots/${planningPreferenceId}`, {
        data,
    });
};

const postUserPlanningPreference = (weekday, start, end, comment, userId) => {
    const data = generatePlanningPreferenceBody(weekday, start, end, comment, userId);
    return axios.post(`${process.env.REACT_APP_API_URL}/unavailable-to-work-time-slots`, {
        data,
    });
};

const delUserPlanningPreference = (planningPreferenceId) => axios.delete(
    `${process.env.REACT_APP_API_URL}/unavailable-to-work-time-slots/${planningPreferenceId}`,
);


export {
    getUserShifts,
    patchDepartment,
    postDepartment,
    patchDeactivateUser,
    patchReactivateUser,
    postRegistrationMail,
    getAuthenticateLoket,
    getUsersCSV,
    postUserToLoketLink,
    postUsersToLoketLink,
    getPermissionGroups,
    patchUserPlanningPreference,
    postUserPlanningPreference,
    delUserPlanningPreference,
};
